import type { Testimonial } from '@/types';
import { testimonials } from '@/data/testimonials';
import { MotionReveal } from '@/components/common/MotionReveal';

interface TestimonialCardProps {
  testimonial: Testimonial;
}

function TestimonialCard({ testimonial }: TestimonialCardProps) {
  return (
    <figure
      className="w-[min(84vw,24rem)] shrink-0 border border-border bg-surface p-5 transition-colors duration-200 hover:border-primary sm:p-6"
      tabIndex={0}
    >
      <div className="flex items-center justify-between gap-4 text-xs">
        <span className="font-semibold text-primary">
          {testimonial.company}
        </span>
        <span
          className="tracking-[0.14em] text-primary"
          aria-label={`${testimonial.rating} od 5 zvezdica`}
        >
          {'★'.repeat(testimonial.rating)}
        </span>
      </div>
      <blockquote className="mt-5 min-h-29 text-sm leading-6 text-text-secondary">
        “{testimonial.quote}”
      </blockquote>
      <figcaption className="mt-5 border-t border-border pt-4">
        <span className="block text-sm font-semibold text-text-primary">
          {testimonial.name}
        </span>
        <span className="mt-1 block text-xs text-text-muted">
          {testimonial.role}
        </span>
      </figcaption>
    </figure>
  );
}

interface MarqueeRowProps {
  items: Testimonial[];
  direction: 'left' | 'right';
  label: string;
}

function MarqueeRow({ items, direction, label }: MarqueeRowProps) {
  return (
    <div
      className="testimonial-marquee overflow-hidden"
      role="region"
      aria-label={label}
      tabIndex={0}
    >
      <div
        className={`testimonial-track testimonial-track-${direction} flex w-max`}
      >
        {[0, 1].map(copyIndex => (
          <div
            key={copyIndex}
            className="flex shrink-0 gap-4 pr-4"
            aria-hidden={copyIndex === 1 ? 'true' : undefined}
          >
            {items.map(testimonial => (
              <TestimonialCard
                key={`${copyIndex}-${testimonial.id}`}
                testimonial={testimonial}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function TestimonialsSection() {
  const upperRow = testimonials.filter((_, index) => index % 2 === 0);
  const lowerRow = testimonials.filter((_, index) => index % 2 === 1);

  return (
    <section
      id="iskustva"
      className="scroll-mt-20 overflow-hidden bg-surface-warm py-20 sm:py-26 lg:py-32"
    >
      <div className="site-container">
        <MotionReveal className="grid gap-5 lg:grid-cols-[0.8fr_0.48fr] lg:items-end lg:gap-24">
          <div>
            <p className="section-label">Iskustva klijenata</p>
            <h2 className="mt-3 max-w-3xl text-balance text-4xl font-semibold leading-[1.08] tracking-[-0.05em] text-text-primary sm:text-5xl lg:text-[3.5rem]">
              Saradnja viđena iz ugla klijenata.
            </h2>
          </div>
          <p className="max-w-lg text-base leading-7 text-text-secondary lg:pb-1">
            Zadržite pokazivač iznad reda da zaustavite kretanje i pročitate
            iskustvo.
          </p>
        </MotionReveal>
      </div>

      <MotionReveal className="relative mt-11 sm:mt-14">
        <div className="space-y-4">
          <MarqueeRow
            items={upperRow}
            direction="left"
            label="Prvi red iskustava klijenata"
          />
          <MarqueeRow
            items={lowerRow}
            direction="right"
            label="Drugi red iskustava klijenata"
          />
        </div>
        <div
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-5 bg-surface-warm/45 backdrop-blur-[5px] sm:w-18"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-5 bg-surface-warm/45 backdrop-blur-[5px] sm:w-18"
          aria-hidden="true"
        />
      </MotionReveal>
    </section>
  );
}
